import * as React from 'react';
import { ButtonLink, Checkbox, checkboxStates } from 'bench-internal-theme';
import { VisuallyHidden } from 'general-ledger/fast-gl/NewGeneralLedger.styles';
import { DataEntry } from 'general-ledger/fast-gl/types';
import { useSelectedEntries } from 'general-ledger/fast-gl/hooks/useSelectedEntries';

type DataEntryTableSelectAllButtonProps = {
  ledgerId: string;
  controlledDataEntries: DataEntry[];
  totalEntries: number;
};

export const DataEntryTableSelectAllButton = ({
  controlledDataEntries,
  ledgerId,
  totalEntries,
}: DataEntryTableSelectAllButtonProps): React.ReactElement => {
  const {
    hasSelectedDataEntryId,
    addSelectedDataEntries,
    removeSelectedDataEntryIds,
    selectedDataEntries,
    hasSelectedLedgerAccountId,
    addSelectedLedgerAccountIds,
    removeSelectedLedgerAccountIds,
    deselectedDataEntries,
    removeDeselectedDataEntryIds,
  } = useSelectedEntries();

  const [areAllDataEntriesSelected, areSomeDataEntriesSelected] = React.useMemo(() => {
    if (hasSelectedLedgerAccountId(ledgerId) && !deselectedDataEntries.get(ledgerId)?.size) {
      return [true, true];
    } else if (hasSelectedLedgerAccountId(ledgerId) && deselectedDataEntries.get(ledgerId)?.size) {
      return [false, true];
    }

    const areAllDataEntriesSelected = controlledDataEntries.every(({ id }) =>
      hasSelectedDataEntryId(id),
    );
    const areSomeDataEntriesSelected = controlledDataEntries.some(({ id }) =>
      hasSelectedDataEntryId(id),
    );
    return [areAllDataEntriesSelected, areSomeDataEntriesSelected];
    // Disabled because we want to recompute this every time the selectedDataEntryIds changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledDataEntries, selectedDataEntries, hasSelectedDataEntryId, deselectedDataEntries]);

  const handleClick = () => {
    // If we don't already have all the data entries loaded, then we need to just select the ledger account
    // and pass that to bulk edit. Otherwise, it's more efficient to pass the data entry ids directly.
    if (totalEntries !== controlledDataEntries.length && selectedDataEntries.size === 0) {
      if (areAllDataEntriesSelected || areSomeDataEntriesSelected) {
        removeSelectedLedgerAccountIds([ledgerId]);
        const entriesToRemove = deselectedDataEntries.get(ledgerId);
        if (entriesToRemove?.size) {
          removeDeselectedDataEntryIds(ledgerId, Array.from(entriesToRemove));
        }
      } else {
        addSelectedLedgerAccountIds([ledgerId]);
      }
      return;
    }

    if (areAllDataEntriesSelected || areSomeDataEntriesSelected) {
      removeSelectedDataEntryIds(controlledDataEntries.map(({ id }) => id));
    } else {
      addSelectedDataEntries(controlledDataEntries);
    }
  };

  return (
    <ButtonLink onClick={handleClick}>
      <Checkbox
        checkboxState={
          (areAllDataEntriesSelected && checkboxStates.CHECKED) ||
          (areSomeDataEntriesSelected && checkboxStates.INDETERMINATE) ||
          checkboxStates.UNCHECKED
        }
        title="Select All"
      />
      <VisuallyHidden>
        {areAllDataEntriesSelected ? 'All' : areSomeDataEntriesSelected ? 'Some' : 'No'} data
        entries are selected in this ledger.
      </VisuallyHidden>
    </ButtonLink>
  );
};
